// src/pages/Ventas/Ventas.tsx

import React, { useState, useEffect} from 'react';
import './Ventas.scss';
import LoadingSpinner from '../../../../components/common/LoadingSpinner';
import { toast } from 'react-toastify';
import { getHistoricalSalesStatisticsByLocalId } from '../../../../crud/management_api/sales_statistics';
import { SalesStatistics } from './types';
import GeneralSales from './components/GeneralSales/GeneralSales';
import ProductStats from './components/ProductStats/ProductStats';
import FrequencyStats from './components/FrequencyStats/FrequencyStats';
import { DayOfWeek, ViewMode } from './components/FrequencyStats/types';
import CompactFilterBar from './components/CompactFilterBar/CompactFilterBar';
import VentasOffCanvas from './components/OffCanvas/OffCanvas';
import { LocalSession } from '../../../../types/LocalSession';
import { MPPageTitle } from '../../../../components/ui';

type Section = 'general' | 'productos' | 'frecuencia';
type TimeRange = 'current' | '7d' | '30d' | 'custom';
type AggregationType = 'by_item' | 'by_user' | 'by_pedido_type' | 'by_menu_category';



interface VentasProps {
  lastSession?: LocalSession | null;
}

const Ventas: React.FC<VentasProps> = ({ lastSession }) => {
  const [loading, setLoading] = useState(false);
  const [stats, setStats] = useState<SalesStatistics[]>([]);
  const [filteredStats, setFilteredStats] = useState<SalesStatistics[]>([]);
  const [section, setSection] = useState<Section>('general');

  // Filtros
  const today = new Date();
  const [showFilters, setShowFilters] = useState(false);
  // Calcular fecha de hace un mes
  const oneMonthAgo = new Date(today);
  oneMonthAgo.setMonth(today.getMonth() - 1);
  const [startDate, setStartDate] = useState(oneMonthAgo.toISOString().split('T')[0]);
  const [endDate, setEndDate] = useState(today.toISOString().split('T')[0]);

  // Nuevo estado para rango de monto
  const [enableAmountRange, setEnableAmountRange] = useState(false);
  const [amountRange, setAmountRange] = useState<[number, number]>([0, 10000]);

  // ProductStats filter states
  const [timeRange, setTimeRange] = useState<TimeRange>('current');
  const [aggregationType, setAggregationType] = useState<AggregationType>('by_item');

  // FrequencyStats filter states
  const [selectedDay, setSelectedDay] = useState<DayOfWeek>('Monday');
  const [viewMode, setViewMode] = useState<ViewMode>('both');

  useEffect(() => {
    let isMounted = true;
    setLoading(true);
    const raw = localStorage.getItem('user');
    const localId = raw ? JSON.parse(raw).local_id ?? 0 : 0;

    getHistoricalSalesStatisticsByLocalId(localId)
      .then(response => {
        if (isMounted) {
          const reversed = [...response.data].reverse();
          setStats(reversed);
        }
      })
      .catch(error => {
        console.error('Error obteniendo estadísticas de ventas: ', error);
        toast.error('Error obteniendo estadísticas de ventas');
      })
      .finally(() => {
        if (isMounted) setLoading(false);
      });

    return () => { isMounted = false; };
  }, []);

  useEffect(() => {
    applyFilters(stats);
    // eslint-disable-next-line
  }, [startDate, endDate, enableAmountRange, amountRange, stats]);

  useEffect(() => {
    // Asegurar que la fecha fin no sea anterior a la de inicio
    const inputs = document.querySelectorAll('input[type="date"]');
    if (inputs.length > 1) {
      const endDateInput = inputs[1] as HTMLInputElement;
      if (endDateInput) {
        endDateInput.min = startDate;
      }
    }
  }, [startDate]);

  // Filtro de datos
  const applyFilters = (data: SalesStatistics[]) => {
    let filtered = data;

    // Filtro por fechas
    if (startDate && endDate) {
      filtered = filtered.filter(s => {
        const statDate = new Date(s.timestamp).toISOString().split('T')[0];
        return statDate >= startDate && statDate <= endDate;
      });
    }

    // Filtro por rango de monto, solo si está habilitado
    if (enableAmountRange) {
      filtered = filtered.filter(
        s => s.total_charged >= amountRange[0] && s.total_charged <= amountRange[1]
      );
    }

    setFilteredStats(filtered);
  };

  // Handlers para cambiar fechas
  const handleStartDateChange = (newDate: string) => {
    setStartDate(newDate);
    if (endDate < newDate) {
      setEndDate(newDate);
    }
  };
  const handleEndDateChange = (newDate: string) => {
    setEndDate(newDate);
  };




  // Encabezado dinámico
  const renderHeader = () => {
    const dynamicTitle = section === 'general'
      ? 'Ventas-General'
      : section === 'productos'
      ? 'Ventas-Productos'
      : section === 'frecuencia'
      ? 'Ventas-Frecuencia'
      : 'Ventas-Usuarios';

    const [mainTitle, subTitle] = dynamicTitle.split('-');

    return (
      <div className="flex flex-col gap-3">
        <MPPageTitle subtitle={subTitle}>{mainTitle}</MPPageTitle>
        
        <div className="mp-button-selector-page-container">
          <div className="mp-button-selector-page-selector">
            <button
              onClick={() => setSection('general')}
              className={`mp-button-selector-page-button ${section === 'general' ? 'active' : 'inactive'}`}
            >
              Generales
            </button>
            <button
              onClick={() => setSection('productos')}
              className={`mp-button-selector-page-button ${section === 'productos' ? 'active' : 'inactive'}`}
            >
              Productos
            </button>
            <button
              onClick={() => setSection('frecuencia')}
              className={`mp-button-selector-page-button ${section === 'frecuencia' ? 'active' : 'inactive'}`}
            >
              Frecuencia
            </button>
          </div>
        </div>

        <CompactFilterBar
          startDate={startDate}
          endDate={endDate}
          onStartDateChange={handleStartDateChange}
          onEndDateChange={handleEndDateChange}
          onShowMobileFilters={() => setShowFilters(true)}
          section={section}
          timeRange={timeRange}
          aggregationType={aggregationType}
          onTimeRangeChange={setTimeRange}
          onAggregationTypeChange={setAggregationType}
          salesData={filteredStats}
          selectedDay={selectedDay}
          viewMode={viewMode}
          onSelectedDayChange={setSelectedDay}
          onViewModeChange={setViewMode}
        />
      </div>
    );
  };

  // Contenido según sección
  const renderContent = () => {
    switch (section) {
      case 'productos':
        return (
          <ProductStats 
            timeRange={timeRange}
            aggregationType={aggregationType}
            onTimeRangeChange={setTimeRange}
            onAggregationTypeChange={setAggregationType}
            startDate={startDate}
            endDate={endDate}
          />
        );
      case 'frecuencia':
        return (
          <FrequencyStats 
            startDate={startDate}
            endDate={endDate}
            selectedDay={selectedDay}
            viewMode={viewMode}
            onSelectedDayChange={setSelectedDay}
            onViewModeChange={setViewMode}
          />
        );
      default:
        return (
          <GeneralSales 
            salesData={filteredStats} 
          />
        );
    }
  };

  return (
    <div className="flex flex-col gap-4 p-3 pb-6 lg:gap-5 lg:p-4 lg:pb-8">
      {renderHeader()}

      <VentasOffCanvas
        show={showFilters}
        onHide={() => setShowFilters(false)}
        section={section}
        startDate={startDate}
        endDate={endDate}
        onStartDateChange={handleStartDateChange}
        onEndDateChange={handleEndDateChange}
        enableAmountRange={enableAmountRange}
        amountRange={amountRange}
        onEnableAmountRangeChange={setEnableAmountRange}
        onAmountRangeChange={setAmountRange}
        timeRange={timeRange}
        aggregationType={aggregationType}
        onTimeRangeChange={setTimeRange}
        onAggregationTypeChange={setAggregationType}
      />

      {loading ? (
        <LoadingSpinner text="Cargando información de ventas..." transparent />
      ) : (
        renderContent()
      )}
    </div>
  );
};

export default Ventas;
